/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable react-hooks/exhaustive-deps */
import { BackArrow } from '@ahamove/icons';
import type { ItemDescription } from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import {
  Button,
  CloseButton,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { openContextModal } from '@mantine/modals';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ModalName } from '@/components/modals';
import { useGetItemTrend } from '@/features/booking/api/special-request';
import { getServiceType } from '@/lib/utils';
import { usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

import 'swiper/css';
import 'swiper/css/bundle';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export interface DescriptionSectionProps {
  onChange: (key: string, value: ItemDescription[]) => void;
}

const ItemDescriptionsSection = ({ onChange }: DescriptionSectionProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { status } = useAuth();
  const { itemDescriptions, createOrderError } = useBookingStore(
    (state) => state
  );
  const [itemDesc, setItemDesc] = useState<ItemDescription[]>(itemDescriptions);
  const { data: trends } = useGetItemTrend({
    enabled: status === 'authenticated',
  });

  const pathname = usePathname();
  const { isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const handleItemDescriptionClick = (code: string) => {
    const newDescriptions = itemDesc.some((desc) => desc?.code === code)
      ? itemDesc.filter((desc) => desc?.code !== code)
      : ([
          ...itemDesc,
          trends?.find((item) => item.code === code),
        ] as ItemDescription[]);
    setItemDesc(newDescriptions);
  };

  useEffect(() => {
    setItemDesc(itemDescriptions);
  }, [itemDescriptions]);

  const debouncedSetItemDesc = useCallback(
    debounce(() => {
      onChange('item_descriptions', itemDesc);
    }, 300),
    [itemDesc]
  );

  useEffect(() => {
    debouncedSetItemDesc();
    return () => {
      debouncedSetItemDesc.cancel();
    };
  }, [debouncedSetItemDesc]);

  const showItemDescriptionsModal = () => {
    const modalName = isTruckService
      ? ModalName.TruckItemDescriptionsList
      : ModalName.BikeItemDescriptionsList;
    openContextModal({
      modal: modalName,
      innerProps: {},
      title: 'Chọn loại hàng hóa',
      h: 'fit-content',
      closeOnClickOutside: false,
      withCloseButton: true,
      closeButtonProps: {
        pos: isMobile ? 'absolute' : 'unset',
        mr: isMobile ? 0 : rem(-10),
        left: rem(12),
        top: isMobile ? 'auto' : rem(8),
        radius: 'xl',

        icon: isMobile ? (
          <BackArrow width={22} height={22} />
        ) : (
          <CloseButton width={22} height={22} />
        ),
      },
      fullScreen: isMobile,
      centered: true,
      maw: rem(440),
      w: '100%',
      styles: {
        root: {
          zIndex: 1,
        },
        title: {
          flex: 1,
          marginLeft: isMobile ? 0 : rem(14),
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 700,
        },
        header: {
          minHeight: isMobile ? rem(45) : rem(56),
          padding: isMobile ? '0px 16px' : '0px 24px',
          borderBottom: `1px solid var(--mantine-color-neutral-0)`,
        },
        body: {
          padding: 0,
        },
        content: {
          minHeight: rem(600),
        },
      },
    });
  };

  const isHighlightBtnOrder = useMemo(
    () =>
      itemDesc.length === 0 ||
      itemDesc.some(
        (item) =>
          !(trends ?? [])
            .slice(0, 3)
            .map((trend) => trend?.code)
            .includes(item?.code)
      ),
    [itemDesc, trends]
  );

  const isEmptyItemCreateOrderError = useMemo(() => {
    return Boolean(
      createOrderError &&
        createOrderError === 'package-detail' &&
        itemDesc.length <= 0
    );
  }, [createOrderError, itemDesc]);

  return (
    <Stack
      gap={8}
      px="md"
      pt={rem(6)}
      pb="md"
      bg={isEmptyItemCreateOrderError ? 'red.0' : 'white'}
    >
      <Title order={isMobile ? 6 : 4} size={isMobile ? 'sm' : 'md'}>
        Loại hàng hóa{' '}
        <Text component="span" c="red">
          *
        </Text>
      </Title>

      <Swiper
        style={{
          overflow: 'visible',
          height: 40,
          width: '100%',
          position: 'relative',
        }}
        spaceBetween={8}
        slidesPerView={'auto'}
        mousewheel={true}
        modules={[Mousewheel]}
      >
        {trends?.length ? (
          <>
            {trends?.slice(0, 3).map((item) => {
              const isSelected = itemDesc.some(
                (desc) => desc?.code === item.code
              );
              return (
                <SwiperSlide key={item.code} style={{ width: 'auto' }}>
                  <Button
                    fw={isSelected ? 600 : 500}
                    radius={rem(40)}
                    variant={isSelected ? 'filled' : 'outline'}
                    c={isSelected ? 'primary' : 'neutral.7'}
                    onClick={() => handleItemDescriptionClick(item.code)}
                    style={{
                      borderColor: isSelected
                        ? 'var(--mantine-color-primary-6)'
                        : 'var(--mantine-color-neutral-4)',
                      backgroundColor: isSelected
                        ? 'var(--mantine-color-primary-2)'
                        : 'transparent',
                      whiteSpace: 'nowrap', // Đảm bảo text không bị wrap
                    }}
                  >
                    <Text
                      size={isMobile ? 'sm' : 'md'}
                      fw={isSelected ? 600 : 500}
                    >
                      {item.keyword}
                    </Text>
                  </Button>
                </SwiperSlide>
              );
            })}
            {trends?.length > 3 ? (
              <SwiperSlide style={{ width: 'auto' }}>
                <Button
                  fw={600}
                  radius={rem(40)}
                  variant={isHighlightBtnOrder ? 'filled' : 'outline'}
                  c={isHighlightBtnOrder ? 'primary' : 'neutral.7'}
                  onClick={showItemDescriptionsModal}
                  style={{
                    borderColor: isHighlightBtnOrder
                      ? 'var(--mantine-color-primary-6)'
                      : 'var(--mantine-color-neutral-4)',
                    backgroundColor: isHighlightBtnOrder
                      ? 'var(--mantine-color-primary-2)'
                      : 'transparent',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Text
                    size={isMobile ? 'sm' : 'md'}
                    fw={isHighlightBtnOrder ? 600 : 500}
                  >
                    Khác
                  </Text>
                </Button>
              </SwiperSlide>
            ) : null}
          </>
        ) : (
          Array(4)
            .fill(0)
            .map((_, index) => (
              <SwiperSlide key={index} style={{ width: 'auto' }}>
                <Skeleton radius={40} w={120} h={34} />
              </SwiperSlide>
            ))
        )}
      </Swiper>
    </Stack>
  );
};
export default ItemDescriptionsSection;
